import { CheckCircle, ChevronDown, Pause } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleAdsPauseKeywords } from 'sprites-types/workflow/tools/googleAdsPauseKeywords'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function KeywordPauseItem({ keyword, className }: { keyword: any; className?: string }) {
  const getStatusColor = (success: boolean) => {
    return success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-center gap-2">
          <Pause className="h-4 w-4 text-orange-600" />
          <div className="flex-1 text-sm font-semibold text-gray-900">Keyword Paused</div>
          <div className={`rounded-full px-2 py-1 text-xs ${getStatusColor(keyword.success)}`}>
            {keyword.success ? 'Success' : 'Failed'}
          </div>
        </div>

        {keyword.resourceName && (
          <div className="mb-3">
            <div className="text-xs text-gray-600">
              Resource: <span className="font-mono text-[10px]">{keyword.resourceName}</span>
            </div>
          </div>
        )}

        {keyword.message && (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="text-xs text-gray-600">{keyword.message}</div>
          </div>
        )}

        {keyword.success && (
          <div className="mt-3 flex items-center gap-2 border-t border-gray-200 pt-2">
            <CheckCircle className="h-3 w-3 text-green-600" />
            <span className="text-xs text-green-600">
              Keyword paused successfully and will stop triggering ads
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

function GoogleAdsPauseKeywordsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsPauseKeywords, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const keywords = Array.isArray(result) ? result : [result]

  if (keywords.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <Pause className="h-5 w-5 text-gray-400" />
          <div className="text-sm text-gray-600">No keywords paused</div>
        </div>
      </div>
    )
  }

  if (keywords.length === 1) {
    return <KeywordPauseItem keyword={keywords[0]} />
  }

  const successfulKeywords = keywords.filter((kw: any) => kw.success).length

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Pause className="h-5 w-5 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                Keyword Pause Results
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {successfulKeywords} of {keywords.length} keywords paused successfully
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {keywords.map((keyword: any, index: number) => (
            <KeywordPauseItem
              key={keyword.resourceName || index}
              keyword={keyword}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGoogleAdsPauseKeywords(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsPauseKeywordsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsPauseKeywords, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const keywords = Array.isArray(result) ? result : [result]

  if (keywords.length === 0) {
    return `## Google Ads Pause Keywords Results

No keywords were paused.

---
`
  }

  const keywordsMarkdown = keywords
    .map((keyword: any) => {
      return `**Success:** ${keyword.success ? 'Yes' : 'No'}
${keyword.resourceName ? `**Resource Name:** ${keyword.resourceName}` : ''}
${keyword.message ? `**Message:** ${keyword.message}` : ''}`
    })
    .join('\n\n---\n\n')

  return `## Google Ads Pause Keywords Results (${keywords.length} keywords)

${keywordsMarkdown}

---
`
}

export const GoogleAdsPauseKeywordsDisplay = createWorkflowToolComponent(
  GoogleAdsPauseKeywordsDisplayComponent,
  downloadGoogleAdsPauseKeywords,
  googleAdsPauseKeywordsToMarkdown
)
